import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

/**CAUTION - To upgrade chart.js or annotation plugin
 * 
 * Make sure two package versions are compatible
 * 
 * currently, doughnut charts are used in
 * 1. Career Planning Home
 * 2. Career planning header
 * 3. Paying tab of school details
*/

Chart.register(...registerables);
Chart.register(annotationPlugin);
// Plugin to draw center text for doughnut charts (avoids annotation plugin scale warnings)
const centerTextPlugin = {
  id: 'centerTextPlugin',
  beforeDraw: (chart) => {
    try {
      // Only run for doughnut charts and when centerText option is enabled
      // @ts-ignore
      const opts = chart.options && chart.options.plugins && chart.options.plugins.centerText;
      if (!opts || chart.config.type !== 'doughnut') {
        return;
      }
      const ctx = chart.ctx;
      const width = chart.width;
      const height = chart.height;
      const text = opts.text || '';
      if (!text) {
        return;
      }
      ctx.save();
      const fontSize = opts.font && opts.font.size ? opts.font.size : 14;
      const fontWeight = opts.font && opts.font.weight ? opts.font.weight : 'bold';
      ctx.font = `${fontWeight} ${fontSize}px sans-serif`;
      ctx.fillStyle = opts.color || '#000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, width / 2, height / 2);
      ctx.restore();
    } catch (e) {
      // fail silently to avoid breaking charts
    }
  }
};
Chart.register(centerTextPlugin);
/**
 * This is a shared component to draw charts in the app to 
 * consolidate individual charts functions across the modules 
 * 
 * Currently, the component can draw 
 *    pie and doughnut chart.
 * 
 * 
 * Required parameters
 *  @Input() chartData: number[];
 
 Optional parameters
  @Input() chartColors: string[];
  @Input() chartType: string = '';
  @Input() chartLabels: string[];
  @Input() chartHeight: string;
  @Input() chartWidth: string;
  @Input() classList: string;
  @Input() cutoff: string = '';
  @Input() centerText: boolean = false; //this is for doughnut ONLY
  Notes: default chart height and width is 100px
 */
@Component({
    selector: 'app-pie-doughnut-chart',
    templateUrl: './pie-doughnut-chart.component.html',
    styleUrls: ['./pie-doughnut-chart.component.css'],
    standalone: false
})
export class PieDoughnutChartComponent implements OnInit {
  @Input() chartType: string = '';
  @Input() chartData: number[] = [];
  @Input() chartColors: string[];
  @Input() chartLabels: string[];

  @Input() chartHeight: string;
  @Input() chartWidth: string;
  @Input() classList: string;
  @Input() cutout: string;
  @Input() centerText: boolean = false;
  //hieght, width
  /** */

  pieDatasets: ChartConfiguration<'pie'>['data']['datasets'] = [];

  doughnutDatasets: ChartConfiguration<'doughnut'>['data']['datasets'];

  //chart options - pie chart  
  pieChartOptions: ChartConfiguration<'pie'>['options'] = {
    responsive: false,
    maintainAspectRatio: false,
    aspectRatio: 2.0,
    plugins: {
      legend: {
        display: false
      }
    }
  };

  //chart option - doughnut chart
  // typed as any so we can provide custom plugin options (centerText) without TypeScript errors
  doughnutChartOptions: any = {};


  /**set default chart dimension */
  canvasHeight: string = '100px';
  canvasWidth: string = '100px';

  /**some modules may use classes */
  canvasClassList: string = '';

  constructor() {

  }

  ngOnInit(): void {
    //set default chart type to pie
    if (this.chartType === '' || !this.chartType) {
      this.chartType = 'pie'
    }

    if (this.chartHeight && this.chartHeight !== '') {
      this.canvasHeight = this.chartHeight

    }
    if (this.chartWidth && this.chartWidth !== '') {
      this.canvasWidth = this.chartWidth
    }

    if (this.chartType === 'doughnut') {
      this.doughnutDatasets = [
        {
          data: this.chartData,
          backgroundColor: this.chartColors
        }
      ];
      if (this.cutout && this.cutout !== '') {
        this.doughnutChartOptions['cutout'] = this.cutout;
      }
    }


    if (this.chartType === 'pie') {
      this.pieDatasets = [
        {
          data: this.chartData,
          backgroundColor: this.chartColors
        }
      ]
    }
    if (this.classList) {
      this.canvasClassList = this.classList;
    }

    const centerText = this.centerText ? `${this.calculatePlanCompletePercentage(this.chartData)}` : '';
    this.doughnutChartOptions = {
      responsive: false,
      cutout: '60%',
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        // Use centerText plugin options to draw text in middle of doughnut
        centerText: {
          display: this.centerText,
          text: centerText,
          color: '#000',
          font: {
            size: this.canvasWidth === '140px' ? 24 : 13,
            weight: 'bold'
          }
        }
      }
    };

  }

  chartClicked() {
    return null;
  }

  chartHovered() {
    return null;
  }
  calculatePlanCompletePercentage(data: number[]): string {
    if (Array.isArray(data) && data.length >= 3) {
      let sum: number = 0;
      data.forEach(item => {
        sum += item;
      })
      return ((data[0] / sum) * 100).toFixed(0) + '%';
    }
  }
  /**
   * 
   * @param changes @Input() chartType: string = '';
    @Input() chartData: number[];
    @Input() chartColors: string[];
    @Input() chartLabels: string[];
    @Input() chartHeight: string;
    @Input() chartWidth: string;
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['chartData'] && this.chartType === 'pie') {
      this.pieDatasets = [
        {
          data: this.chartData,
          backgroundColor: this.chartColors
        }
      ]

    }
    if (changes['chartData'] && this.chartType === 'doughnut') {
      this.doughnutDatasets = [
        {
          data: this.chartData,
          backgroundColor: this.chartColors
        }
      ];

    }
    if (changes['chartData'] && this.chartType === 'doughnut') {
      const centerText = this.centerText ? `${this.calculatePlanCompletePercentage(this.chartData)}` : '';
      this.doughnutChartOptions = {
        responsive: false,
        cutout: '60%',
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          centerText: {
            display: this.centerText,
            text: centerText,
            color: '#000',
            font: {
              size: this.canvasWidth === '140px' ? 24 : 14,
              weight: 'bold'
            }
          }
        }
      };
    }
  }
  // ngAfterViewInit() {
  //   if (this.chartHeight && this.chartHeight !== '') {
  //     this.canvasHeight = this.chartHeight

  //   }
  //   if (this.chartWidth && this.chartWidth !== '') {
  //     this.canvasWidth = this.chartWidth
  //   }

  //   if (this.chartType === 'doughnut') {
  //     this.doughnutDatasets = [
  //       {
  //         data: this.chartData,
  //         backgroundColor: this.chartColors
  //       }
  //     ];
  //     console.log('this.doughnutDatasets:', this.doughnutDatasets)
  //     if (this.cutout && this.cutout !== '') {
  //       this.doughnutChartOptions['cutout'] = this.cutout;
  //     }
  //   }


  //   if (this.chartType === 'pie') {
  //     this.pieDatasets = [
  //       {
  //         data: this.chartData,
  //         backgroundColor: this.chartColors
  //       }
  //     ]
  //   }
  //   if (this.classList) {
  //     this.canvasClassList = this.classList;
  //   }

  // }

}
